// Globals
import { Trans, useTranslation } from 'react-i18next';

// Store
import { useAppDispatch, useAppState } from '@/store';
import { selectStepsProgresses, stopPropertyAd } from '../../feature';

// Models
import { AdStepProgress, PropertyAdState } from '@/models/propertyProgress.model';
import { StepType } from '@/models/step.model';

// Components
import {
  ResetAdStepButton,
  StepCard,
  StopConfirmationModal,
  StyledButtonsWrap,
  StyledRow,
  StyledText,
} from '../../components';
import { Button } from '@/ui';
import { showSuccessToast } from '@/components';
import { ArrowRoundIcon, CalendarSecondaryIcon, StopCrossIcon } from '@/icons';

// Helpers
import { useModal } from '@/hooks';
import { getEntityId, renderDate } from '@/utils';
import { DateFormats } from '@/constants/dates';
import { canStopAd, isAdOperationInProgress, isAdPublished } from '../../helpers/getStatuses';
import { finalStatuses } from '../../helpers';

type TAdProgressContainerProps = {
  hideResetButton?: boolean;
};

const AdProgressContainer = ({ hideResetButton = false }: TAdProgressContainerProps) => {
  const { t } = useTranslation('property');
  const dispatch = useAppDispatch();
  const stepsProgresses = useAppState(selectStepsProgresses);
  const adProgress = stepsProgresses?.[StepType.AD] as AdStepProgress;
  const { isOpened: stopConfirmOpened, openModal: openStopConfirm, closeModal: closeStopConfirm } = useModal();

  const advertisement = adProgress?.advertisement;
  const operationInProgress = isAdOperationInProgress(advertisement);
  const adPublished = isAdPublished(advertisement);

  const adUrl = advertisement?.ad_url ?? advertisement?.preview_url;

  const stateLabel = advertisement?.state
    ? t(`ad_state_${advertisement.state}`, { defaultValue: advertisement.state.toUpperCase() })
    : '';

  const handleAdStop = async () => {
    closeStopConfirm();
    try {
      await dispatch(stopPropertyAd({ propertyId: getEntityId(adProgress?.property_id) })).unwrap();
      closeStopConfirm();
      showSuccessToast({ message: t('ad_order_stop_success') });
    } catch (e) {
      console.warn(e);
    } finally {
      closeStopConfirm();
    }
  };

  if (!adProgress) return null;

  return (
    <StepCard
      title={t('step_ad')}
      status={adProgress?.status}
      actions={
        <StyledButtonsWrap type={'default'}>
          {!hideResetButton && finalStatuses.includes(adProgress.status) && (
            <ResetAdStepButton
              propertyId={getEntityId(adProgress?.property_id)}
              stepName={StepType.AD}
              icon={<ArrowRoundIcon />}
            />
          )}
          {!hideResetButton && canStopAd(advertisement) && (
            <Button
              type="default"
              shape="round"
              onClick={openStopConfirm}
              icon={<StopCrossIcon />}
              disabled={operationInProgress}
            >
              {t('ad_stop_btn')}
            </Button>
          )}
        </StyledButtonsWrap>
      }
    >
      {!!advertisement && (
        <StyledRow>
          <StyledText>
            <Trans
              i18nKey="ad_status"
              ns="property"
              components={{
                strong: <strong />,
              }}
              values={{ status: stateLabel }}
            />
          </StyledText>
        </StyledRow>
      )}
      {advertisement?.state === PropertyAdState.INACTIVE && !!advertisement?.inactive_reason && (
        <StyledRow>
          <StyledText>
            {t(`ad_inactive_reason_${advertisement.inactive_reason}`, {
              defaultValue: advertisement.inactive_reason,
            })}
          </StyledText>
        </StyledRow>
      )}
      {!!advertisement?.in_progress && (
        <StyledRow>
          <StyledText>
            {t('ad_operation_in_progress', {
              operation: t(`ad_operation_${advertisement.in_progress}`, {
                defaultValue: advertisement.in_progress,
              }),
            })}
          </StyledText>
        </StyledRow>
      )}
      {adPublished && !!advertisement?.publication_period_ends && (
        <StyledRow>
          <StyledText>
            <CalendarSecondaryIcon />
            <Trans
              i18nKey="ad_advertisement_period"
              ns="property"
              values={{
                date: renderDate(advertisement.publication_period_ends, DateFormats.WITH_DOTS),
              }}
            />
          </StyledText>
        </StyledRow>
      )}
      {!!adUrl && (
        <StyledRow>
          <StyledText>
            <a href={adUrl} target={'_blank'} rel="noreferrer">
              {adUrl}
            </a>
          </StyledText>
        </StyledRow>
      )}
      <StopConfirmationModal isOpened={stopConfirmOpened} onConfirm={handleAdStop} onClose={closeStopConfirm} />
    </StepCard>
  );
};

export default AdProgressContainer;
